import { useCallback } from 'react';
import { Modal } from '@/components/ui/Modal';
import { useSessionStore } from '@/stores';
import { TokenService } from '@/services/auth/tokenService';
import { loginRequest } from '@/config/msal.config';
import { msalInstance } from '@/config/msal.instance';

/**
 * Modal shown when all silent token recovery attempts have failed.
 * Keeps the user on their current page and lets them re-authenticate
 * without losing context (scroll position, form data, etc.).
 */
export function SessionExpiredModal() {
  const { isSessionExpired, setSessionExpired } = useSessionStore();

  const handleSignIn = useCallback(async () => {
    // Clear stale tokens before re-authenticating
    TokenService.clearTokens();

    // Redirect to Microsoft login - after success, MSAL will
    // exchange the ID token and land the user back on their current page
    try {
      await msalInstance.loginRedirect({
        ...loginRequest,
        redirectStartPage: window.location.href,
      });
    } catch (error) {
      console.error('Login redirect failed:', error);
      window.location.href = '/login';
    }
  }, []);

  const handleLogout = useCallback(() => {
    TokenService.clearTokens();
    setSessionExpired(false);
    window.location.href = '/login';
  }, [setSessionExpired]);

  return (
    <Modal
      isOpen={isSessionExpired}
      onClose={() => {}}
      showCloseButton={false}
      showBackdrop={true}
      width="max-w-[448px]"
    >
      <div className="flex flex-col gap-6 items-center py-2">
        {/* Icon */}
        <div className="flex items-center justify-center size-8">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle
              cx="16"
              cy="16"
              r="14"
              stroke="#fa8c16"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M16 10V17"
              stroke="#fa8c16"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="16" cy="21" r="1" fill="#fa8c16" />
          </svg>
        </div>

        {/* Title and Message */}
        <div className="flex flex-col gap-2 items-center text-center">
          <p
            className="font-roboto font-medium text-xl leading-7 text-[#3a3542]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            Session Expired
          </p>
          <p
            className="font-roboto font-normal text-base leading-6 text-[#59565c] max-w-[400px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            Your session has expired due to inactivity. Please sign in again to
            continue.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col w-full gap-0">
          <div className="bg-[#f4f3f7] h-[2px] w-full" />
          <div className="flex items-center justify-center gap-8 pt-3">
            <button
              onClick={handleLogout}
              className="font-roboto font-medium text-base leading-[22px] text-[#59565c] hover:opacity-80 transition-opacity"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Log Out
            </button>
            <button
              onClick={handleSignIn}
              className="font-roboto font-medium text-base leading-[22px] text-[#7956d8] hover:opacity-80 transition-opacity"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Sign In Again
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
